import React from 'react';
import { TollFeeLocation } from '../services/api/routeAnalysis';
import { useLanguage } from '../contexts/LanguageContext';

interface TollFeeLocationsListProps {
  tollFeeLocations: TollFeeLocation[];
  showActualRoute?: boolean;
  showOptimalRoute?: boolean;
}

const TollFeeLocationsList: React.FC<TollFeeLocationsListProps> = ({ 
  tollFeeLocations,
  showActualRoute = true,
  showOptimalRoute = true
}) => {
  const { t } = useLanguage();
  
  // Debug logging
  console.log('[TollFeeLocationsList] Received tollFeeLocations:', tollFeeLocations);
  console.log('[TollFeeLocationsList] Length:', tollFeeLocations?.length);
  console.log('[TollFeeLocationsList] showActualRoute:', showActualRoute);
  console.log('[TollFeeLocationsList] showOptimalRoute:', showOptimalRoute);
  
  // Format currency for Vietnam
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
currency: 'VND' 
    }).format(value);
  };

  // Filter toll locations based on visible routes
  // IMPORTANT: The backend logic has a limitation where it only checks isPassed for actual route.
  // If a toll is on the actual route, it's marked isPassed=true and optimal route check is skipped.
  // This means tolls that are on BOTH routes are only marked as "passed" (actual route).
  // 
  // To fix this on the frontend side:
  // - Show tolls with isPassed=true when actual route is visible
  // - Show ALL tolls when optimal route is visible (both isPassed=true and isPassed=false)
  //   because a toll marked as "passed" might also be on the optimal route
  const getFilteredTollLocations = () => {
    if (!tollFeeLocations || tollFeeLocations.length === 0) {
      return { actualRouteTolls: [], optimalRouteTolls: [] };
    }

    // If both routes are hidden, show nothing
    if (!showActualRoute && !showOptimalRoute) {
      return { actualRouteTolls: [], optimalRouteTolls: [] };
    }

    // Actual route tolls: Only show tolls that were actually passed (isPassed=true)
 const actualRouteTolls = showActualRoute 
      ? tollFeeLocations.filter(t => t.isPassed)
      : [];

    // Optimal route tolls: Show ALL tolls
    // This includes both:
    // - Tolls only on optimal route (isPassed=false)
    // - Tolls on both routes (isPassed=true) - these were skipped by backend optimal check
    const optimalRouteTolls = showOptimalRoute 
  ? tollFeeLocations
      : [];

    return { actualRouteTolls, optimalRouteTolls };
  };

  const { actualRouteTolls, optimalRouteTolls } = getFilteredTollLocations();
  const totalActualFees = actualRouteTolls.reduce((sum, t) => sum + t.fee, 0);
  const totalOptimalFees = optimalRouteTolls.reduce((sum, t) => sum + t.fee, 0);

  console.log('[TollFeeLocationsList] Actual route tolls:', actualRouteTolls.length);
  console.log('[TollFeeLocationsList] Optimal route tolls:', optimalRouteTolls.length);

  // Check if there are any tolls to display based on current route visibility
  const hasVisibleTolls = actualRouteTolls.length > 0 || optimalRouteTolls.length > 0;

  return (
    <div className="card shadow-sm">
      <div className="card-header" style={{
     background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
   color: 'white'
      }}>
        <div className="d-flex justify-content-between align-items-center">
    <h6 className="mb-0">
     <i className="bi bi-cash-coin me-2"></i>
   {t('toll.fee.locations')}
          </h6>
      {hasVisibleTolls && (
            <span className="badge bg-light text-dark rounded-pill px-3">
{(actualRouteTolls.length || 0) + (optimalRouteTolls.length || 0)}
     </span>
   )}
    </div>
      </div>
      <div className="card-body p-3" style={{ maxHeight: '600px', overflowY: 'auto' }}>
   {!hasVisibleTolls ? (
     <div className="text-center py-5 text-muted">
    <i className="bi bi-info-circle d-block mb-3" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
    <h6 className="text-muted">
  {!showActualRoute && !showOptimalRoute 
              ? t('enable.route.display')
    : t('no.toll.fees.detected')}
     </h6>
     <p className="small mb-0">
     {!showActualRoute && !showOptimalRoute 
      ? t('toggle.route.visibility')
    : t('no.toll.on.visible.routes')}
      </p>
    </div>
        ) : (
    <>
     {/* Actual Route Tolls */}
      {showActualRoute && actualRouteTolls.length > 0 && (
         <div className="mb-4">
         <div className="d-flex align-items-center mb-3">
  <div className="flex-grow-1">
       <h6 className="mb-0 text-danger">
        <i className="bi bi-check-circle-fill me-2"></i>
        {t('actual.route.tolls')}
  </h6>
        </div>
    <span className="badge bg-danger rounded-pill px-3">
     {actualRouteTolls.length}
          </span>
        </div>

    <div className="list-group">
         {actualRouteTolls.map((toll, index) => (
   <div 
      key={`actual-${toll.tollFeeId}-${index}`} 
  className="list-group-item border-start-0 border-end-0"
style={{ 
    borderLeft: '4px solid #dc3545',
         transition: 'all 0.2s ease'
   }}
             >
           <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
           <div className="d-flex align-items-center mb-2">
           <div 
        className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center me-2 fw-bold"
     style={{ minWidth: '24px', height: '24px', fontSize: '0.7rem' }}
                >
              ✓
      </div>
  <div className="fw-bold text-danger">
          {toll.locationName}
</div>
          </div>
         
  <small className="text-muted d-block mb-1">
    <i className="bi bi-signpost me-1"></i>
          {toll.locationType}
    </small>
              
        {toll.address && (
  <small className="text-muted d-block mb-1">
        <i className="bi bi-geo-alt me-1"></i>
  {toll.address}
    </small>
 )}
   
         {toll.latitude && toll.longitude && (
          <small className="text-muted font-monospace d-block" style={{ fontSize: '0.7rem' }}>
          <i className="bi bi-crosshair me-1"></i>
 {toll.latitude.toFixed(4)}, {toll.longitude.toFixed(4)}
         </small>
         )}
             </div>
        
  <div className="text-end ms-3">
    <div className="fw-bold text-danger h6 mb-0">
   {formatCurrency(toll.fee)}
          </div>
    </div>
 </div>
   </div>
        ))}
                </div>

         <div className="card bg-danger bg-opacity-10 border-danger mt-3">
     <div className="card-body p-2">
     <div className="d-flex justify-content-between align-items-center">
   <strong className="text-danger">{t('total.actual.fees')}</strong>
     <strong className="text-danger h6 mb-0">{formatCurrency(totalActualFees)}</strong>
    </div>
  </div>
     </div>
    </div>
   )}

      {/* Optimal Route Tolls */}
  {showOptimalRoute && optimalRouteTolls.length > 0 && (
      <div>
     <div className="d-flex align-items-center mb-3">
       <div className="flex-grow-1">
     <h6 className="mb-0 text-success">
   <i className="bi bi-geo-alt-fill me-2"></i>
   {t('optimal.route.tolls')}
      </h6>
    </div>
   <span className="badge bg-success rounded-pill px-3">
     {optimalRouteTolls.length}
              </span>
   </div>

  <div className="list-group">
          {optimalRouteTolls.map((toll, index) => (
            <div 
          key={`optimal-${toll.tollFeeId}-${index}`} 
          className="list-group-item border-start-0 border-end-0"
          style={{ 
 borderLeft: '4px solid #28a745',
   transition: 'all 0.2s ease'
         }}
          >
       <div className="d-flex justify-content-between align-items-start">
         <div className="flex-grow-1">
         <div className="d-flex align-items-center mb-2">
                <div 
         className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center me-2 fw-bold"
     style={{ minWidth: '24px', height: '24px', fontSize: '0.7rem' }}
     >
         {index + 1}
</div>
    <div className="fw-bold text-success">
         {toll.locationName}
  </div>
              </div>
               
     <small className="text-muted d-block mb-1">
    <i className="bi bi-signpost me-1"></i>
        {toll.locationType}
      </small>
    
              {toll.address && (
      <small className="text-muted d-block mb-1">
        <i className="bi bi-geo-alt me-1"></i>
    {toll.address}
 </small>
    )}
      
                  {toll.latitude && toll.longitude && (
        <small className="text-muted font-monospace d-block" style={{ fontSize: '0.7rem' }}>
         <i className="bi bi-crosshair me-1"></i>
 {toll.latitude.toFixed(4)}, {toll.longitude.toFixed(4)}
     </small>
      )}
             </div>
         
      <div className="text-end ms-3">
         <div className="fw-bold text-success h6 mb-0">
             {formatCurrency(toll.fee)}
   </div>
      </div>
      </div>
          </div>
         ))}
      </div>

        <div className="card bg-success bg-opacity-10 border-success mt-3">
     <div className="card-body p-2">
       <div className="d-flex justify-content-between align-items-center">
        <strong className="text-success">{t('total.optimal.fees')}</strong>
     <strong className="text-success h6 mb-0">{formatCurrency(totalOptimalFees)}</strong>
 </div>
   </div>
   </div>
       </div>
      )}

 {/* Comparison if both routes are shown */}
      {showActualRoute && showOptimalRoute && actualRouteTolls.length > 0 && optimalRouteTolls.length > 0 && (
   <div className="card bg-light border-0 mt-4">
  <div className="card-body p-3">
   <h6 className="text-muted mb-3">
    <i className="bi bi-bar-chart me-2"></i>
      {t('fee.comparison')}
    </h6>
        <div className="d-flex justify-content-between align-items-center mb-2">
     <span className="text-muted">{t('difference')}:</span>
           <span className={`fw-bold h6 mb-0 ${totalActualFees > totalOptimalFees ? 'text-danger' : 'text-success'}`}>
    {totalActualFees > totalOptimalFees ? '+' : ''}
       {formatCurrency(totalActualFees - totalOptimalFees)}
     </span>
     </div>
    {totalActualFees > totalOptimalFees && (
  <small className="text-muted d-block">
 <i className="bi bi-info-circle me-1"></i>
        {t('you.could.save')}
     </small>
   )}
      </div>
   </div>
    )}
  </>
        )}
   </div>
    </div>
  );
};

export default TollFeeLocationsList;
